<template>
  <div>
    <DataTable
      ref="dataTable"
      :columns="columnList"
      defaultColumnOrderDir="ASC"
      :defaultColumnOrderIndex="0"
      :url="dataURL"
      :filterOptions="filterOptions"
      :hasDateRangeFilter="true"
      exportTitle="Purchase Orders">
      <template v-slot:title>
        <slot name="title" />
      </template>
      <template v-slot:tools>
        <slot name="tools" />
      </template>
      <template v-slot:tableHeaderCell="tableHeaderCell">
        <span v-if="tableHeaderCell.data == addRunsheetColumnIndex && selectionEnabled" class="ml-2">
          <FormButton class="btn-xxs btn-default mr-1" @click="selectAllVisible">Select All</FormButton>
          <FormButton class="btn-xxs btn-default ml-1" @click="unselectAllVisible">Unselect All</FormButton>
        </span>
      </template>
      <template v-slot:row="row">
        <tr :key="row.data.id">
          <td class="table-data-nav-link">
            <LinkPurchaseOrder :name="row.data.sequenceNumber ? '#'+row.data.sequenceNumber : 'Pending'" :id="row.data.id" :newTab="openNewTab" />
          </td>
          <td>
            {{row.data.contactName}}
          </td>
          <td>
            <LabelBadge :labelClass="`label-po-type-${row.data.type}`">{{row.data.type | formatLookupName(PurchaseOrderTypeLookup)}}</LabelBadge>
          </td>
          <td>
            <LabelBadge :labelClass="`label-po-status-${row.data.status}`">{{row.data.status | formatLookupName(PurchaseOrderStatusTypeLookup)}}</LabelBadge>
            <LabelBadge class="label-order-tasks-assigned ml-1" v-if="row.data.isActiveTasksAssigned"><i class="far fa-fw fa-calendar-check"></i> Tasks Assigned</LabelBadge>
          </td>
          <td>
            <FormButton class="btn-default btn-xxs" @click="viewPurchaseOrderSummary($event, row.data.id)">View</FormButton>
          </td>
          
          <td class="text-right" v-if="selectionEnabled">
            <FormCheckbox :value="isPurchaseOrderSelected(row.data.id)" @change="updatePurchaseOrderSelection(row.data.id, $event)" />
          </td>
        </tr>
      </template>
    </DataTable>

    <PurchaseOrderSummaryPopover ref="poSummaryPopover" />
  </div>
</template>

<script>
import _ from 'lodash'
import Utils from '@/utils/utils'
import AccountLocationDisplay from '@/modules/admin/components/accountLocationDisplay'
import PurchaseOrderSummaryPopover from '@/modules/purchasing/components/purchaseOrderSummaryPopover'

import { DespatchTypes, PurchaseOrderTypes, PurchaseOrderTypeLookup, PurchaseOrderStatusTypes, PurchaseOrderStatusTypeLookup } from '@/store/types'

export default {
  name: 'TaskPurchaseOrderPickupDataTable',
  model: {
    event: 'update'
  },
  components: {
    AccountLocationDisplay,
    PurchaseOrderSummaryPopover
  },
  props: {
    openNewTab: {
      type: Boolean,
      default: false
    },
    value: Array,
    filterAccountLocationId: Number,
    selectionEnabled: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {
          despatchType: DespatchTypes.pickup
        }
      }
      if(this.filterAccountLocationId)
        params.filter.accountLocationId = this.filterAccountLocationId

      return '/purchasing/purchaseorders?' + $.param(params)
    },
    addRunsheetColumnIndex() {
      return 9
    }
  },
  data() {
    let columnList = _.compact([
        { title: "Number", data: "sequenceNumber" },
        { title: "Contact", data: "contact.name" },
        { title: "Type", data: "type" },
        { title: "Status", data: "status" },
        { title: "Info", data: "", sortable: false, export: false },
        this.selectionEnabled ? { title: "Add Runsheet", data: "", sortable: false, export: false } : null,
    ])

    let filterOptions = [
      {
        field: 'status',
        name: 'Status',
        options: [
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingApproval), value: PurchaseOrderStatusTypes.pendingApproval, class: 'label-po-status-pendingApproval' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingConfirmation), value: PurchaseOrderStatusTypes.pendingConfirmation, class: 'label-po-status-pendingConfirmation' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingShipping), value: PurchaseOrderStatusTypes.pendingShipping, class: 'label-po-status-pendingShipping' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingReceival), value: PurchaseOrderStatusTypes.pendingReceival, class: 'label-po-status-pendingReceival' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.receiving), value: PurchaseOrderStatusTypes.receiving, class: 'label-po-status-receiving' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.pendingPickup), value: PurchaseOrderStatusTypes.pendingPickup, class: 'label-po-status-pendingPickup' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.completed), value: PurchaseOrderStatusTypes.completed, class: 'label-po-status-completed' },
          { name: Utils.getLookupName(PurchaseOrderStatusTypeLookup, PurchaseOrderStatusTypes.cancelled), value: PurchaseOrderStatusTypes.cancelled, class: 'label-po-status-cancelled' }
        ]
      },
      {
        field: 'isActiveTasksAssigned',
        name: 'Tasks Assigned',
        columnIndex: 0,
        options: [
          { name: 'Tasks Assigned', value: '1', class: 'label-order-tasks-assigned' },
          { name: 'No Tasks Assigned', value: '0', class: 'label-order-tasks-noneassigned' }
        ]
      },
    ]    

    return {
      filterOptions: filterOptions,
      columnList: columnList,
      selectedPOMap: {}
    }
  },
  watch: {
    value: function() {
      this.updateSelectedPurchaseOrderMap()
    }
  },
  created() {
    this.PurchaseOrderTypeLookup = PurchaseOrderTypeLookup
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
  },
  mounted() {
    this.updateSelectedPurchaseOrderMap()

    // Apply default filters
    this.$nextTick(() => {
      let defaultFilters = _.compact([
        { value: PurchaseOrderStatusTypes.pendingPickup, field: 'status' },
        { value: '0', field: 'isActiveTasksAssigned' }
      ])

      this.$refs.dataTable.applySelectedFilters(defaultFilters)
    })
  },
  methods: {
    isPurchaseOrderSelected(purchaseOrderId) {
      return this.selectedPOMap[purchaseOrderId]
    },
    updatePurchaseOrderSelection(purchaseOrderId, selected) {
      this.selectedPOMap[purchaseOrderId] = selected
      this.updatePurchaseOrderList()
    },
    selectAllVisible() {
      this.$refs.dataTable.getRowListData().forEach(purchaseOrder => {
        this.$set(this.selectedPOMap, purchaseOrder.id, true)
      })
      this.updatePurchaseOrderList()
    },
    unselectAllVisible() {
      this.$refs.dataTable.getRowListData().forEach(purchaseOrder => {
        this.$set(this.selectedPOMap, purchaseOrder.id, false)
      })
      this.updatePurchaseOrderList()
    },
    updateSelectedPurchaseOrderMap() {
      this.selectedPOMap = {}
      if(this.value) {
        _.uniq(this.value).forEach(purchaseOrderId => {
          this.selectedPOMap[purchaseOrderId] = true
        })
      }
    },
    updatePurchaseOrderList() {
      this.$emit('update', Object.keys(this.selectedPOMap).filter(purchaseOrderId => this.selectedPOMap[purchaseOrderId]).map(purchaseOrderId => parseInt(purchaseOrderId)))
    },
    viewPurchaseOrderSummary(ev, purchaseOrderId) {
      this.$refs.poSummaryPopover.displayFromElement(ev.target, purchaseOrderId)
    }
  }
};
</script>
